import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import DarkModeToggle from '../components/DarkModeToggle';
import {
  Shield,
  FileText,
  Upload,
  TrendingUp,
  QrCode,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  LogOut,
  LayoutDashboard,
  Briefcase,
  MessageSquare
} from 'lucide-react';

export default function Dashboard() {
  const { user, token, logout } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [trustScore, setTrustScore] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('cv');
  const [hasPersonalDetails, setHasPersonalDetails] = useState(false);
  const [personalDetailsComplete, setPersonalDetailsComplete] = useState(false);
  const [cvHistory, setCvHistory] = useState([]);
  const [credits, setCredits] = useState(0);
  const [cvData, setCvData] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchTrustScore();
    if (user?.role === 'candidate') {
      checkPersonalDetails();
      fetchCvHistory();
      fetchCredits();
      fetchCvData();
    }
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchTrustScore = async () => {
    try {
      const response = await axios.get(`${API}/score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrustScore(response.data);
    } catch (error) {
      // No score yet
    }
  };

  const checkPersonalDetails = async () => {
    try {
      const response = await axios.get(`${API}/profile/personal-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend returns data directly (not nested in 'details')
      if (response.data && response.data.user_id) {
        setHasPersonalDetails(true);
        const details = response.data;
        
        // Check if key fields are filled
        const isComplete = !!(
          details.full_name &&
          details.email &&
          details.date_of_birth &&
          details.nationality &&
          details.current_address
        );
        setPersonalDetailsComplete(isComplete);
      } else {
        setHasPersonalDetails(false);
        setPersonalDetailsComplete(false);
      }
    } catch (error) {
      setHasPersonalDetails(false);
      setPersonalDetailsComplete(false);
    }
  };

  const fetchCvHistory = async () => {
    try {
      const response = await axios.get(`${API}/cv/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCvHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch CV history:', error);
    }
  };

  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const fetchCvData = async () => {
    try {
      const response = await axios.get(`${API}/cv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Check if CV data actually exists (not just a message saying no CV found)
      if (response.data && !response.data.message && response.data.user_id) {
        setCvData(response.data);
      } else {
        setCvData(null);
      }
    } catch (error) {
      // CV data doesn't exist yet
      setCvData(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/documents/upload?document_type=${docType}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Document uploaded successfully!');
      fetchDocuments();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" data-testid="dashboard">
      {/* Navbar */}
      <nav className="glass border-b dark:border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <img src="/assets/refrx-logo-black.png" alt="RefrX" className="h-10 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="nav-dashboard-button"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              {(user?.role === 'recruiter' || user?.role === 'admin') && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/recruiter')}
                  data-testid="nav-recruiter-button"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Recruiter
                </Button>
              )}
              {user?.role === 'admin' && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin')}
                  data-testid="nav-admin-button"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              {user?.role === 'candidate' ? (
                <div 
                  onClick={() => navigate('/personal-details')}
                  className="flex items-center gap-2 px-3 py-1 rounded-lg bg-white dark:bg-gray-800 border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  data-testid="profile-badge"
                >
                  <span className="text-sm font-medium dark:text-gray-200">{user?.full_name}</span>
                  <span className="text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded capitalize">
                    {user?.role?.replace('_', ' ')}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-white dark:bg-gray-800 border dark:border-gray-700">
                  <span className="text-sm font-medium dark:text-gray-200">{user?.full_name}</span>
                  <span className="text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded capitalize">
                    {user?.role?.replace('_', ' ')}
                  </span>
                </div>
              )}
              {user?.role === 'candidate' && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/feedback')}
                  className="flex items-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline">Feedback</span>
                </Button>
              )}
              <DarkModeToggle />
              <Button variant="outline" onClick={logout} data-testid="logout-button">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Beta Banner - Candidates Only */}
        {user?.role === 'candidate' && (
          <div className="mb-4 p-4 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white rounded-xl shadow-lg border-2 border-white dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <span className="text-2xl">🚀</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">BETA VERSION - Limited Features Available</h3>
                <p className="text-sm mb-2">
                  <strong>Currently Working:</strong> ATS-Friendly CV & Cover Letter Generation
                </p>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/30">
                  <p className="text-sm font-semibold mb-1">💰 Launch Pricing Alert!</p>
                  <p className="text-xs">
                    Credits are <strong>extra cheap</strong> right now due to new launch. Prices may increase soon!
                  </p>
                  <p className="text-xs mt-1 font-bold">
                    ✨ Your credits are PERMANENT and will NEVER expire - Grab them now!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Verification Status Banner - Candidates Only */}
        {user?.role === 'candidate' && (
          <div className="mb-6">
            {user?.is_verified ? (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                <div className="flex-1">
                  <p className="font-semibold text-green-900 dark:text-green-300">Profile Verified ✓</p>
                  <p className="text-sm text-green-700 dark:text-green-400">Your identity has been verified by our admin team</p>
                </div>
              </div>
            ) : user?.verification_submitted ? (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-3">
                <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                <div className="flex-1">
                  <p className="font-semibold text-yellow-900 dark:text-yellow-300">Verification Pending</p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">Your profile is under review by our admin team</p>
                </div>
              </div>
            ) : !hasPersonalDetails && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <div className="flex-1">
                  <p className="font-semibold text-blue-900 dark:text-blue-300">Complete Your Verification</p>
                  <p className="text-sm text-blue-700 dark:text-blue-400">Add personal details and submit for verification to unlock full features</p>
                </div>
                <Button 
                  onClick={() => navigate('/personal-details')}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="complete-verification-button"
                >
                  Start Verification
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          {user?.role === 'candidate' && (
            <>
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/personal-details')}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Personal Details</CardTitle>
                  <Shield className="w-4 h-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold capitalize">
                    {personalDetailsComplete ? (
                      <span className="text-green-600">Complete</span>
                    ) : hasPersonalDetails ? (
                      <span className="text-yellow-600">Incomplete</span>
                    ) : (
                      <span className="text-gray-600">Not Started</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {personalDetailsComplete ? 'Click to update' : 'Add details now'}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/cv-builder')}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">CV / Resume</CardTitle>
                  <Briefcase className="w-4 h-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {cvData ? (
                      <span className="text-green-600">Complete</span>
                    ) : (
                      <span className="text-gray-600">Not Started</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {cvData ? 'Update your CV' : 'Build your CV now'}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/cv-history')}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">CV History</CardTitle>
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {cvHistory.length > 0 ? `${cvHistory.length} CV${cvHistory.length > 1 ? 's' : ''}` : 'No CVs'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {cvHistory.length > 0 ? 'Click to view & download' : 'Generate your first CV'}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/credits')}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Credits & Referrals</CardTitle>
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{credits} Credits</div>
                  <p className="text-xs text-gray-500 mt-1">
                    {credits < 5 ? 'Low balance - Refer friends!' : 'Refer friends & earn more'}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
          
          <Card className="card-hover">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">TRUST-AI Score</CardTitle>
              <TrendingUp className="w-4 h-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {trustScore ? `${trustScore.score.toFixed(1)}/100` : 'N/A'}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {trustScore ? 'Complete profile' : 'Take questionnaire'}
              </p>
            </CardContent>
          </Card>

          {user?.role === 'candidate' && (
            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">KYC Status</CardTitle>
                <Shield className="w-4 h-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold capitalize">
                  {user?.kyc_status === 'approved' ? '✓ Approved' : 
                   user?.kyc_status === 'pending' ? 'Pending' :
                   user?.kyc_status === 'rejected' ? 'Rejected' : 'Not Started'}
                </div>
                <p className="text-xs text-gray-500 mt-1">Identity verification</p>
              </CardContent>
            </Card>
          )}

          <Card className="card-hover">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Documents</CardTitle>
              <FileText className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{documents.length}</div>
              <p className="text-xs text-gray-500 mt-1">
                {documents.filter(d => d.status === 'verified').length} verified
              </p>
            </CardContent>
          </Card>

          {user?.role === 'candidate' && (
            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Passport</CardTitle>
                <QrCode className="w-4 h-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <Button
                  size="sm"
                  onClick={() => navigate('/passport')}
                  className="w-full bg-purple-600 hover:bg-purple-700 mt-2"
                  data-testid="view-passport-button"
                >
                  View Passport
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* TRUST-AI Score Card */}
            {!trustScore ? (
              <Card className="border-2 border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-200">
                    <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Complete Your TRUST-AI Assessment
                  </CardTitle>
                  <CardDescription className="dark:text-gray-400">
                    Build your employability score through our AI-powered questionnaire
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => navigate('/questionnaire')}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    data-testid="start-questionnaire-button"
                  >
                    Start Questionnaire
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="dark:bg-gray-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-200">
                    <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Your TRUST-AI Score
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-6">
                    <div className="text-5xl font-bold gradient-text mb-2">
                      {trustScore.score.toFixed(1)}
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">out of 100</p>
                  </div>

                  <Progress value={trustScore.score} className="h-2" />

                  {trustScore.ai_analysis && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="font-semibold text-sm mb-2 text-blue-900 dark:text-blue-300">AI Analysis</h4>
                      <p className="text-sm text-blue-800 dark:text-blue-400">{trustScore.ai_analysis}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(trustScore.breakdown).map(([key, value]) => (
                      <div key={key} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {key.replace('_', ' ')}
                        </p>
                        <p className="text-lg font-semibold">
                          {typeof value === 'number' ? value.toFixed(1) : value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => navigate('/questionnaire')}
                    className="w-full"
                    data-testid="retake-questionnaire-button"
                  >
                    Retake Assessment
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Documents Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Documents
                </CardTitle>
                <CardDescription>Upload and manage your credentials</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload Section */}
                <div className="p-4 border-2 border-dashed rounded-lg">
                  <div className="flex items-center gap-4">
                    <select
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm"
                      data-testid="document-type-select"
                    >
                      <option value="certificate">Certificate</option>
                      <option value="id_document">ID Document</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="flex-1">
                      <input
                        type="file"
                        id="file-upload"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploading}
                        data-testid="file-upload-input"
                      />
                      <label htmlFor="file-upload" className="block">
                        <div className={`w-full px-4 py-2 border rounded-lg text-center cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <Upload className="w-4 h-4 inline mr-2" />
                          {uploading ? 'Uploading...' : 'Upload Document'}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Documents List */}
                <div className="space-y-2">
                  {documents.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No documents uploaded yet</p>
                  ) : (
                    documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                        data-testid={`document-item-${doc.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(doc.status)}
                          <div>
                            <p className="font-medium text-sm">{doc.filename}</p>
                            <p className="text-xs text-gray-500 capitalize">{doc.document_type}</p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(doc.status)}>{doc.status}</Badge>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/passport')}
                  data-testid="sidebar-passport-button"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  View Digital Passport
                </Button>
                {(user?.role === 'recruiter' || user?.role === 'admin') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate('/recruiter')}
                    data-testid="sidebar-recruiter-button"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Browse Recruiters
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Profile Completeness</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Documents</span>
                    <span>{documents.length > 0 ? '✓' : '○'}</span>
                  </div>
                  <Progress value={documents.length > 0 ? 100 : 0} className="h-1" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>TRUST-AI Score</span>
                    <span>{trustScore ? '✓' : '○'}</span>
                  </div>
                  <Progress value={trustScore ? 100 : 0} className="h-1" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>KYC Verification</span>
                    <span>{user?.kyc_status === 'verified' ? '✓' : '○'}</span>
                  </div>
                  <Progress value={user?.kyc_status === 'verified' ? 100 : 0} className="h-1" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}